import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";

import { FormControl, FormControlLabel, Radio, RadioGroup, Tooltip } from "@mui/material";

import { AgCharts } from "ag-charts-react";
import { type AgChartOptions, ModuleRegistry, CategoryAxisModule, LegendModule, NumberAxisModule, DonutSeriesModule, BarSeriesModule, LineSeriesModule } from "ag-charts-community";
ModuleRegistry.registerModules([ CategoryAxisModule, LegendModule, NumberAxisModule, DonutSeriesModule, BarSeriesModule, LineSeriesModule ]);

import refreshIcon from "@user/assets/images/common/refresh.svg";
import chevronRightIcon from "@user/assets/images/common/chevron-right.svg";

import kutil, { type PageInfo } from "@modules/kutil"; 
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import dayjs from "dayjs";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";

import "./dashboard.scoped.scss";

interface State {
  isContinueSearch: boolean,
  today: string
}

const defState: State = {
  isContinueSearch: true,
  today: dayjs().format("YYYYMMDD") // FIXME 날짜 변경 로직 필요
};

interface SearchParams {
}

const defSearchParams: SearchParams = {
};

interface Result {
  orderInfo: any,
  orderInfos: any[],
  inInfo: any,
  outInfo: any,
  outTimeInfos: any[],
  outDateInfos: any[],
  stockInfos: any[],
  zoneInfo: any,
  cellInfo: any,
  skuInfos: any[],
  backupInfo: any,
  expireStockQty: string,
  workInfo: any,
  workStationInfos: any[]
}

const defResult: Result = {
  orderInfo: {},
  orderInfos: [],
  inInfo: {},
  outInfo: {},
  outTimeInfos: [],
  outDateInfos: [],
  stockInfos: [],
  zoneInfo: {},
  cellInfo: {},
  skuInfos: [],
  backupInfo: {},
  expireStockQty: "",
  workInfo: {},
  workStationInfos: []
};

function Dashboard(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const chartOptions1 = useMemo<AgChartOptions>(() => ({
    data: result.orderInfos,
    series: [
      { type: "bar", xKey: "formattedStatDate", yKey: "inQty", yName: "입고", stacked: true, fill: "#051766", cornerRadius: 4 },
      { type: "bar", xKey: "formattedStatDate", yKey: "outQty", yName: "출고", stacked: true, fill: "#57bbeb", cornerRadius: 4 },
    ],
    padding: {
      top: 0,
      right: 10,
      bottom: 0,
      left: 10,
    },
    axes: {
      x: {
        type: "category",
        position: "bottom",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#666" },
        line: { enabled: true },
        gridLine: { enabled: false },
      },
      y: {
        type: "number",
        position: "left",
        label: { enabled: false },
        line: { enabled: false },
        gridLine: { enabled: false },
      }
    },
    legend: {
      enabled: false
    },
    height: 90,
  }), [result]);

  const chartOptions2 = useMemo<AgChartOptions>(() => {
    const percent = result.inInfo.compPercent ?? 0;
    const compValue = Math.max(percent);
    const waitValue = Math.max(100 - percent);

    return {
      data: [
        { label: "입고대기", value: waitValue },
        { label: "입고완료", value: compValue },
      ],
      padding: {
        top: 5,
        right: 0,
        bottom: 5,
        left: 0,
      },
      series: [{
        type: "donut",
        calloutLabelKey: "label",
        calloutLabel: { enabled: false },
        angleKey: "value",
        fills: ["#e5e5e5", "#051766"],
        innerRadiusRatio: 0.6,
        innerLabels: [
          { text: percent + "%", fontWeight: "bold", fontSize: 22, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#051766" },
          { text: "", spacing: 8 },
        ],
        innerCircle: { fill: "#ffffff" },
      }],
      legend: { enabled: false },
      tooltip: { enabled: false },
      height: 184,
    };
  }, [result]);

  const HOUR_KEYS = [
    "h08","h09","h10","h11","h12","h13","h14","h15","h16",
    "h17","h18","h19","h20","h21","h22","h23","h24",
    "h01","h02","h03","h04","h05","h06","h07"
  ];
  const HOUR_LABELS = [
    "8","9","10","11","12","13","14","15","16",
    "17","18","19","20","21","22","23","24",
    "1","2","3","4","5","6","7"
  ];
  const chartData = useMemo(() => {
    return HOUR_KEYS.map((hk, i) => {
      const row: any = { hour: HOUR_LABELS[i] };
      result.outTimeInfos.forEach((info: any) => {
        row[info.inoutType] = info[hk];
      });
      return row;
    });
  }, [result]);
  const chartOptions3 = useMemo<AgChartOptions>(() => ({
    data: chartData,
    series: [
      { type: "line", xKey: "hour", yKey: "출고", yName: "출고", stroke: "#28794e", marker: { fill: "#fff", stroke: "#28794e", size: 8 }, interpolation: { type: "smooth" } },
    ],
    padding: {
      top: 8,
      right: 10,
      bottom: 8,
      left: 10 ,
    },
    axes: {
      x: {
        type: "category",
        position: "bottom",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#666" },
        line: { enabled: false },
        gridLine: { enabled: false },
      },
      y: {
        type: "number",
        position: "left",
        label: { enabled: false },
        line: { enabled: false },
        gridLine: { enabled: false },
      }
    },
    legend: {
      enabled: false
    },
    height: 88,
  }), [chartData]);

  const chartOptions4 = useMemo<AgChartOptions>(() => ({
    data: result.outDateInfos,
    series: [
      { type: "bar", xKey: "formattedStatDate", yKey: "outCompQty", yName: "출고", fill: "#57bbeb", cornerRadius: 4 },
    ],
    padding: {
      top: 0,
      right: 20,
      bottom: 0,
      left: 20,
    },
    axes: {
      x: {
        type: "category",
        position: "bottom",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#666" },
        line: { enabled: true },
        gridLine: { enabled: false },
      },
      y: {
        type: "number",
        position: "left",
        label: { enabled: false },
        line: { enabled: false },
        gridLine: { enabled: false },
      }
    },
    legend: {
      enabled: false
    },
    height: 88,
  }), [result]);

  const chartOptions5 = useMemo<AgChartOptions>(() => {
    const percent = result.zoneInfo.skuCellPercent ?? 0;
    const useValue = Math.max(percent);
    const totalValue = Math.max(100 - percent);

    return {
      data: [
        { label: "적재가능ITEM", value: totalValue },
        { label: "적재ITEM", value: useValue },
      ],
      padding: {
        top: 5,
        right: 0,
        bottom: 5,
        left: 0,
      },
      series: [{
        type: "donut",
        calloutLabelKey: "label",
        calloutLabel: { enabled: false },
        angleKey: "value",
        fills: ["#e5e5e5", "#28794e"],
        innerRadiusRatio: 0.6,
        innerLabels: [
          { text: percent + "%", fontWeight: "bold", fontSize: 22, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#28794e " },
          { text: "", spacing: 8 },
        ],
        innerCircle: { fill: "#ffffff" },
      }],
      legend: { enabled: false },
      tooltip: { enabled: false },
      height: 184,
    };
  }, [result]);

  const chartOptions6 = useMemo<AgChartOptions>(() => ({
    data: result.workStationInfos,
    series: [
      { type: "bar", xKey: "formattedWorkStationName", yKey: "inQty", yName: "입고", stacked: true, fill: "#051766", cornerRadius: 4 },
      { type: "bar", xKey: "formattedWorkStationName", yKey: "outQty", yName: "출고", stacked: true, fill: "#57bbeb", cornerRadius: 4 },
    ],
    padding: {
      top: 10,
      right: 20,
      bottom: 0,
      left: 20,
    },
    axes: {
      x: {
        type: "category",
        position: "bottom",
        label: { enabled: true, fontFamily: "HanjinGroupSans, Malgun Gothic, sans-serif", color: "#666" },
        line: { enabled: true },
        gridLine: { enabled: false },
      },
      y: {
        type: "number",
        position: "left",
        label: { enabled: false },
        line: { enabled: false },
        gridLine: { enabled: false },
      }
    },
    legend: {
      enabled: false
    },
    height: 120,
  }), [result]);

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval>;
    if (state.isContinueSearch) {
      intervalId = setInterval(() => {
        doSearch();
      }, 5000);
    }
    return () => clearInterval(intervalId);
  }, [state.isContinueSearch, isActiveTab]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const data = await kutil.sendGet("/api/common/dashboard.data?mode=search", {today: state.today}, {});
      const orderInfo = data.workReqInfos[data.workReqInfos.length - 1];
      data.workReqInfos.forEach((info: any) => {
        info.formattedStatDate = kutil.date2string(info.statDate, "MM/dd");
      });

      const todayCompInfo = data.workReqCompInfos[data.workReqCompInfos.length - 1];
      const yesterdayCompInfo = data.workReqCompInfos[data.workReqCompInfos.length - 2];
      data.workReqCompInfos.forEach((info: any) => {
        info.formattedStatDate = kutil.date2string(info.statDate, "MM/dd");
      });
      const outInfo: any = {};
      outInfo.outCompQty = todayCompInfo.outCompQty;
      outInfo.outCompDiffQty = Number(todayCompInfo.outCompQty) - Number(yesterdayCompInfo.outCompQty);

      const inInfo: any = {};
      inInfo.waitQty = data.inWaitWorkQty;
      inInfo.compQty = todayCompInfo.inCompQty;
      const inTotal = Number(inInfo.waitQty) + Number(inInfo.compQty);
      inInfo.compPercent = inTotal === 0 ? 0 : Math.floor((inInfo.compQty / inTotal) * 100);

      const zoneInfo: any = data.zoneInfos?.[0] ?? {
        skuCellQty: 0,
        skuCellUseQty: 0,
        skuCellNoUseQty: 0,
        skuCellPercent: 0,
      };

      const cellInfo: any = (data.cellInfos ?? []).find((row: any) => row.totalYn === "Y") ?? {
        inWorkQty: 0,
        outWorkQty: 0,
        auditWorkQty: 0,
        emptyCellQty: 0,
        disableCellQty: 0,
        errorCellQty: 0,
      };

      const backupInfo: any = data.backupWorkInfos?.[0] ?? {
        inQty: 0,
        outQty: 0,
      };

      const workInfo: any = {};
      workInfo.forceCompleteTotalQty = data.forceCompleteTotalQty;
      workInfo.forceCompleteFailQty = data.forceCompleteFailQty;
      workInfo.tempWorkMatchTotalQty = data.tempWorkMatchTotalQty;
      workInfo.tempWorkMatchFailQty = data.tempWorkMatchFailQty;
      workInfo.erpIfLogErrorQty = data.erpIfLogErrorQty;

      data.workStationInfos.forEach((info: any) => {
        info.formattedWorkStationName = commonCode.getCodeValue("workStationId", info.workStationId)
      });

      setResult({
        orderInfo: orderInfo,
        orderInfos: data.workReqInfos,
        inInfo: inInfo,
        outInfo: outInfo,
        outTimeInfos: data.workReqHourInfos,
        outDateInfos: data.workReqCompInfos,
        stockInfos: data.stockInfos,
        zoneInfo: zoneInfo,
        cellInfo: cellInfo,
        skuInfos: data.skuInfos,
        backupInfo: backupInfo,
        expireStockQty: data.expireStockQty,
        workInfo: workInfo,
        workStationInfos: data.workStationInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      <div className="dashboard-contents">
        {/* 상단 정보 */}
        <div>
          <p>{ kutil.date2string(state.today, "yyyy.MM.dd.(E)") }</p>
          <Tooltip title="새로고침">
            <button className="icon-button bd" onClick={doSearch}><img src={refreshIcon} alt="새로고침" /></button>
          </Tooltip>
          <FormControl>
            <RadioGroup
              row
              value={String(state.isContinueSearch)}
              onChange={(e) => {setState(prev => ({ ...prev, isContinueSearch: e.target.value === "true" }))}}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="자동" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="수동" />
            </RadioGroup>
          </FormControl>
        </div>
        {/*// 상단 정보 */}
        {/* 하단 정보 */}
        <div>
          {/* 작업 지시 정보 */}
          <div className="db-item01">
            <p>작업 지시 정보</p>
            <div>
              <ul>
                <li>
                  <span>신규 입고 지시</span>
                  <p>
                    <em>{ valueFormatter.number(result.orderInfo.inQty) }</em>
                    건
                  </p>
                </li>
                <li>
                  <span>신규 출고 지시</span>
                  <p>
                    <em>{ valueFormatter.number(result.orderInfo.outQty) }</em>
                    건
                  </p>
                </li>
              </ul>
              {/* 차트 */}
              <div>
                <AgCharts options={chartOptions1} style={{ width: "100%" }} />
              </div>
            </div>
          </div>
          {/*// 작업 지시 정보 */}
          {/* 입고 정보 */}
          <div className="db-item02">
            <p>입고 정보</p>
            <div>
              <ul>
                <li>
                  <span>입고 대기</span>
                  <p>
                    <em>{ valueFormatter.number(result.inInfo.waitQty) }</em>
                    건
                  </p>
                </li>
                <li>
                  <span>입고 완료</span>
                  <p>
                    <em>{ valueFormatter.number(result.inInfo.compQty) }</em>
                    건
                  </p>
                </li>
              </ul>
              {/* 차트 */}
              <div>
                <AgCharts options={chartOptions2} style={{ width: "100%" }} />
              </div>
            </div>
          </div>
          {/*// 입고 정보 */}
          {/* 출고 정보 */}
          <div className="db-item03">
            <p>출고 정보</p>
            <div>
              <ul>
                <li>
                  <span>출고 완료</span>
                  <p>
                    <em>{ valueFormatter.number(result.outInfo.outCompQty) }</em>
                    건
                  </p>
                </li>
                <li>
                  <span>출고량 추이</span>
                  <p>
                    {Number(result.outInfo.outCompDiffQty) > 0 && (
                      <em className="increase">↑{result.outInfo.outCompDiffQty}</em>
                    )}
                    {Number(result.outInfo.outCompDiffQty) < 0 && (
                      <em className="decrease">↓{Math.abs(Number(result.outInfo.outCompDiffQty))}</em>
                    )}
                    {Number(result.outInfo.outCompDiffQty) === 0 && (
                      <em>{result.outInfo.outCompDiffQty}</em>
                    )}
                    건
                  </p>
                </li>
              </ul>
              {/* 차트 */}
              <div>
                <div>
                  <AgCharts options={chartOptions3} style={{ width: "100%" }} />
                </div>
                <div>
                  <AgCharts options={chartOptions4} style={{ width: "100%" }} />
                </div>
              </div>
            </div>
          </div>
          {/*// 출고 정보 */}
          {/* 재고 정보 */}
          <div className="db-item04">
            <p>재고 정보</p>
            <div>
              <ul>
                <li>
                  <span>ERP 재고 비교</span>
                  <div>
                    <p>{ valueFormatter.number(result.stockInfos[0]?.qty) }개 품목 불일치</p>
                    <Tooltip title="바로가기">
                      <button className="icon-button" onClick={() => router.navigateByPath("/stock/inventory_compare")}>
                        <img src={chevronRightIcon} alt=" 바로가기" />
                      </button>
                    </Tooltip>
                  </div>
                </li>
                <li>
                  <span>SKU 총 CELL 수</span>
                  <div>
                    <p>{ valueFormatter.number(result.zoneInfo.skuCellQty) }건</p>
                  </div>
                </li>
                <li>
                  <span>SKU 적재 ITEM</span>
                  <div>
                    <p>{ valueFormatter.number(result.zoneInfo.skuCellUseQty) }건</p>
                  </div>
                </li>
                <li>
                  <span>SKU 적재 가능 ITEM</span>
                  <div>
                    <p>{ valueFormatter.number(result.zoneInfo.skuCellNoUseQty) }건</p>
                  </div>
                </li>
                <li>
                  <span>SKU 저장률</span>
                </li>
              </ul>
              {/* 차트 */}
              <div>
                <AgCharts options={chartOptions5} style={{ width: "100%" }} />
              </div>
            </div>
          </div>
          {/*// 재고 정보 */}
          {/* CELL 정보 */}
          <div className="db-item05">
            <p>
              CELL 정보
              <Tooltip title="바로가기">
                <button className="icon-button" onClick={() => router.navigateByPath("/stock/cell_info")}>
                  <img src={chevronRightIcon} alt=" 바로가기" />
                </button>
              </Tooltip>
            </p>
            <div>
              <div>
                <span>입고작업</span>
                <p>
                  <em>{ valueFormatter.number(result.cellInfo.inWorkQty) }</em>
                  개
                </p>
              </div>
              <div>
                <span>출고작업</span>
                <p>
                  <em>{ valueFormatter.number(result.cellInfo.outWorkQty) }</em>
                  개
                </p>
              </div>
              <div>
                <span>실사작업</span>
                <p>
                  <em>{ valueFormatter.number(result.cellInfo.auditWorkQty) }</em>
                  개
                </p>
              </div>
              <div>
                <span>공CELL</span>
                <p>
                  <em>{ valueFormatter.number(result.cellInfo.emptyCellQty) }</em>
                  개
                </p>
              </div>
              <div>
                <span>금지CELL</span>
                <p>
                  <em>{ valueFormatter.number(result.cellInfo.disableCellQty) }</em>
                  개
                </p>
              </div>
              <div>
                <span>오류CELL</span>
                <p>
                  {Number(result.cellInfo.errorCellQty) > 0 && (
                    <em className="error">{result.cellInfo.errorCellQty}</em>
                  )}
                  {Number(result.cellInfo.errorCellQty) === 0 && (
                    <em>{result.cellInfo.errorCellQty}</em>
                  )}
                  개
                </p>
              </div>
            </div>
          </div>
          {/*// CELL 정보 */}
          {/* BackUp 작업 */}
          <div className="db-item06">
            <p>
              BackUp 작업
              <Tooltip title="바로가기">
                <button className="icon-button" onClick={() => router.navigateByPath("/inoutbound/backup_work")}>
                  <img src={chevronRightIcon} alt=" 바로가기" />
                </button>
              </Tooltip>
            </p>
            <div>
              <ul>
                <li>
                  <span>입고 작업</span>
                  <p>
                    <em>{ valueFormatter.number(result.backupInfo.inQty) }</em>
                    건
                  </p>
                </li>
                <li>
                  <span>출고 작업</span>
                  <p>
                    <em>{ valueFormatter.number(result.backupInfo.outQty) }</em>
                    건
                  </p>
                </li>
              </ul>
            </div>
          </div>
          {/*// BackUp 작업 */}
          {/* 오류 및 미처리 작업 */}
          <div className="db-item07">
            <p>오류 및 미처리 작업</p>
            <div>
              <ul>
                <li>
                  <span>강제완료 I/F (발생/미결)</span>
                  <div>
                    <p>{ valueFormatter.number(result.workInfo.forceCompleteTotalQty) }/{ valueFormatter.number(result.workInfo.forceCompleteFailQty) }건</p>
                    <Tooltip title="바로가기">
                      <button className="icon-button" onClick={() => router.navigateByPath("/inoutbound/force_complete_list")}>
                        <img src={chevronRightIcon} alt=" 바로가기" />
                      </button>
                    </Tooltip>
                  </div>
                </li>
                <li>
                  <span>임시작업 매칭 (발생/미결)</span>
                  <div>
                    <p>{ valueFormatter.number(result.workInfo.tempWorkMatchTotalQty) }/{ valueFormatter.number(result.workInfo.tempWorkMatchFailQty) }건</p>
                    <Tooltip title="바로가기">
                      <button className="icon-button" onClick={() => router.navigateByPath("/inoutbound/temp_work_list")}>
                        <img src={chevronRightIcon} alt=" 바로가기" />
                      </button>
                    </Tooltip>
                  </div>
                </li>
                <li>
                  <span>ERP I/F 오류</span>
                  <div>
                    <p>{ valueFormatter.number(result.workInfo.erpIfLogErrorQty) }건</p>
                    <Tooltip title="바로가기">
                      <button className="icon-button" onClick={() => router.navigateByPath("/log/erp_if_log_list")}>
                        <img src={chevronRightIcon} alt=" 바로가기" />
                      </button>
                    </Tooltip>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          {/*// 오류 및 미처리 작업 */}
          {/* 시효 만료 예정 */}
          <div className="db-item08">
            <p>
              시효 만료 임박
              <Tooltip title="바로가기">
                <button className="icon-button" onClick={() => router.navigateByPath("/report/expiration_status")}>
                  <img src={chevronRightIcon} alt=" 바로가기" />
                </button>
              </Tooltip>
            </p>
            <div>
              <em>{ valueFormatter.number(result.expireStockQty) }</em>
              건
            </div>
          </div>
          {/*// 시효 만료 예정 */}
          {/* 공SKU 정보 */}
          <div className="db-item09">
            <p>공SKU 정보</p>
            <div>
              <div>Pallet</div>
              <div>
                <span>대형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty1) }</em>
                  개
                </p>
              </div>
              <div>
                <span>중형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty2) }</em>
                  개
                </p>
              </div>
              <div>
                <span>소형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty3) }</em>
                  개
                </p>
              </div>
              <div>Bucket</div>
              <div>
                <span>중형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty4) }</em>
                  개
                </p>
              </div>
              <div>
                <span>소형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty5) }</em>
                  개
                </p>
              </div>
              <div>
                <span>초소형</span>
                <p>
                  <em>{ valueFormatter.number(result.skuInfos[0]?.qty6) }</em>
                  개
                </p>
              </div>
            </div>
          </div>
          {/*// 공SKU 정보 */}
          {/* 작업대별 작업 현황 */}
          <div className="db-item10">
            <p>작업대별 작업 현황</p>
            <div>
              <AgCharts options={chartOptions6} style={{ width: "100%" }} />
            </div>
          </div>
          {/*// 작업대별 작업 현황 */}
        </div>
        {/*// 하단 정보 */}
      </div>
    </>
  );
}

export default React.memo(Dashboard);
